/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {Box} from '@salesforce/retail-react-app/app/components/shared/ui'

/**
 * Render the children in the DOM but visually hide them on desktop
 * @param children - isomorphic components used within a responsive design
 */
export const HideOnDesktop = ({children, isOpen}) => {
    const menuRef = useRef()
    const [searchHeader, setSearchHeader] = useState(null)
    const [searchTabletHeader, setSearchTabletHeader] = useState(null)
    useEffect(() => {
        // We are handling the header height dynamically instead of using static values in CSS for mobile and tablet devices.
        const header = document.querySelector('.cr-header')?.offsetHeight
        const headerMobile = document.querySelector('.header-block')?.offsetHeight
        const svodHeight = document.querySelector('.header-promo-and-svod-block')?.offsetHeight
        const search = document.querySelector('.search-input')?.offsetHeight

        const headerHeight = headerMobile + svodHeight - search
        setSearchHeader(headerHeight)
        setSearchTabletHeader(header)

        if (isOpen && menuRef?.current) {
            menuRef.current.scrollTop = 0
        }
    }, [isOpen])

    return (
        <Box
            ref={menuRef}
            display={{base: 'block', md: 'none'}}
            className={'mobile-list-menu-items'}
            left={isOpen ? '0' : '-100%'}
            overflowY="auto"
            maxHeight="100vh"
            top={
                isOpen
                    ? {base: `${searchHeader}px`, sm: `${searchTabletHeader}px`}
                    : `${searchHeader}px`
            }
            height={
                isOpen
                    ? {
                          base: `calc(100vh - ${searchHeader}px)`,
                          sm: `calc(100vh - ${searchTabletHeader}px)`
                      }
                    : ``
            }
        >
            {children}
        </Box>
    )
}
HideOnDesktop.propTypes = {children: PropTypes.node, isOpen: PropTypes.bool}

/**
 * Render the children in the DOM but visually hide them on mobile
 * @param children - isomorphic components used within a responsive design
 */
export const HideOnMobile = ({children}) => (
    <Box display={{base: 'none', md: 'block'}}>{children}</Box>
)
HideOnMobile.propTypes = {children: PropTypes.node}

export default {HideOnMobile, HideOnDesktop}
